import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShareNetwork, GearSix, PaperPlaneTilt } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

export const CapiCard = () => {
  const [s, setS] = useState(null);
  const [open, setOpen] = useState(false);
  const [pixels, setPixels] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [pixelId, setPixelId] = useState("");
  const [revenue, setRevenue] = useState("3000");
  const [sendHot, setSendHot] = useState(true);
  const [hotList, setHotList] = useState(["Hot Lead", "Hot"]);
  const [crmStatuses, setCrmStatuses] = useState([]);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);

  const load = () => {
    api.get("/capi/settings").then(({ data }) => {
      setS(data); setEnabled(!!data.enabled); setPixelId(data.pixel_id || ""); setRevenue(String(data.revenue_per_admission || 3000));
      setSendHot(data.send_hot_leads !== false); setHotList(data.hot_values || ["Hot Lead", "Hot"]);
    }).catch(() => setS({}));
    api.get("/capi/statuses").then(({ data }) => setCrmStatuses(data.statuses || [])).catch(() => {});
  };

  const isHot = (v) => hotList.some((h) => h.toLowerCase() === v.toLowerCase());
  const toggleHot = (v) => setHotList((l) => (isHot(v) ? l.filter((h) => h.toLowerCase() !== v.toLowerCase()) : [...l, v]));
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!open || pixels !== null) return;
    api.get("/capi/pixels").then(({ data }) => setPixels(data.pixels || [])).catch((e) => {
      toast.error(e.response?.data?.detail || "Could not load pixels from Meta"); setPixels([]);
    });
  }, [open, pixels]);

  const save = async () => {
    if (enabled && !pixelId) { toast.error("Pick the Meta Pixel to send admissions to"); return; }
    setBusy(true);
    try {
      await api.put("/capi/settings", { enabled, pixel_id: pixelId || null, revenue_per_admission: Number(revenue) || 3000, send_hot_leads: sendHot, hot_values: hotList.join(", ") });
      toast.success(enabled ? "Admissions (₹" + (Number(revenue) || 3000) + " Purchase)" + (sendHot ? " + Hot Leads" : "") + " will be sent to Meta" : "Settings saved");
      setOpen(false); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save", { duration: 8000 });
    } finally { setBusy(false); }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/capi/send-now");
      const parts = [];
      if (data.admissions) parts.push(`${data.admissions} admission`);
      if (data.hot) parts.push(`${data.hot} hot-lead`);
      toast.success(parts.length
        ? `Sent ${parts.join(" + ")} event${data.sent === 1 ? "" : "s"} to Meta${data.skipped ? ` · ${data.skipped} skipped (expired/unmatchable lead IDs)` : ""}`
        : data.skipped ? `Nothing sendable — ${data.skipped} skipped (expired/unmatchable lead IDs)` : "Nothing new to send");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed", { duration: 9000 });
    } finally { setSending(false); }
  };

  const fmtT = (t) => { const d = new Date(t); return isNaN(d) ? "" : d.toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); };

  return (
    <div className="rounded-xl border border-[#E5E7EB] bg-white p-5" data-testid="capi-source-card">
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="capi-dialog">
          <DialogHeader><DialogTitle className="font-heading font-extrabold">Meta Conversions API</DialogTitle></DialogHeader>
          <div className="space-y-4 text-sm">
            <p className="text-xs text-[#8a90a0]">Every admission (positive outcome from Zoho / sheet / manual mark) is sent back to Meta as a server-side <b>Purchase</b> event with its value. Hot leads from your CRM can also be sent as <b>HotLead</b> events — so Meta's Conversion Leads optimization learns your full funnel, not just cheap leads.</p>
            <div className="flex items-center justify-between rounded-lg border border-[#E5E7EB] px-4 py-3">
              <div className="text-xs font-bold text-[#0A0D14]">Send admissions to Meta</div>
              <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="capi-enable-switch" />
            </div>
            <div>
              <label className="text-xs font-bold text-[#0A0D14]">Meta Pixel / Dataset</label>
              <Select value={pixelId} onValueChange={setPixelId}>
                <SelectTrigger className="mt-1 h-9 text-xs font-semibold" data-testid="capi-pixel-select">
                  <SelectValue placeholder={pixels === null ? "Loading pixels…" : pixels.length ? "Pick a pixel" : "No pixels found on your ad accounts"} />
                </SelectTrigger>
                <SelectContent>{(pixels || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.name || p.id} · {p.id}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-bold text-[#0A0D14]">Revenue per admission (₹)</label>
              <Input value={revenue} onChange={(e) => setRevenue(e.target.value.replace(/[^\d.]/g, ""))} className="mt-1 h-9 text-xs" data-testid="capi-revenue-input" />
              <p className="text-[10px] text-[#8a90a0] mt-1">Also used for Revenue & ROAS in the Conversion Report.</p>
            </div>
            <div className="rounded-lg border border-[#E5E7EB] px-4 py-3 space-y-2.5">
              <div className="flex items-center justify-between">
                <div className="text-xs font-bold text-[#0A0D14]">Also send Hot Leads <span className="font-normal text-[#8a90a0]">(HotLead event, ₹0)</span></div>
                <Switch checked={sendHot} onCheckedChange={setSendHot} data-testid="capi-hot-switch" />
              </div>
              {sendHot && (
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wide text-[#8a90a0]">Which CRM statuses count as Hot?</label>
                  {crmStatuses.length > 0 ? (
                    <>
                      <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="capi-hot-status-chips">
                        {crmStatuses.map((st) => (
                          <button type="button" key={st.value} onClick={() => toggleHot(st.value)}
                            data-testid={`capi-status-chip-${st.value.replace(/\s+/g, "-").toLowerCase()}`}
                            className={`h-7 px-2.5 rounded-full text-[11px] font-bold border transition-colors ${isHot(st.value)
                              ? "bg-[#FF6B35] border-[#FF6B35] text-white"
                              : "bg-white border-[#E5E7EB] text-[#525866] hover:border-[#FF6B35]/50"}`}>
                            {st.value} <span className={isHot(st.value) ? "opacity-80" : "text-[#8a90a0]"}>({st.count})</span>
                          </button>
                        ))}
                      </div>
                      <p className="text-[10px] text-[#8a90a0] mt-1.5">These statuses come from your CRM (synced via Zoho) for leads created from Facebook. Tap to select — selected statuses are sent to Meta as HotLead events.</p>
                    </>
                  ) : (
                    <>
                      <Input value={hotList.join(", ")} onChange={(e) => setHotList(e.target.value.split(",").map((v) => v.trim()).filter(Boolean))}
                        placeholder="Hot Lead, Hot" className="mt-1 h-9 text-xs" data-testid="capi-hot-values-input" />
                      <p className="text-[10px] text-[#8a90a0] mt-1">No CRM statuses synced yet — run a Zoho sync and this becomes a tap-to-select list. Comma separated for now.</p>
                    </>
                  )}
                </div>
              )}
            </div>
            <Button onClick={save} disabled={busy} className="w-full rounded-full bg-[#0A0D14] hover:bg-black text-white font-bold h-10" data-testid="capi-save-btn">
              {busy ? "Saving…" : "Save"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <div className="flex items-center gap-3">
        <span className="h-10 w-10 rounded-lg bg-[#0052FF]/10 flex items-center justify-center"><ShareNetwork size={20} weight="duotone" className="text-[#0052FF]" /></span>
        <div className="mr-auto min-w-0">
          <div className="font-bold text-sm text-[#0A0D14]">Meta Conversions API</div>
          <div className="text-[11px] text-[#8a90a0] truncate" data-testid="capi-status-line">
            {!s ? "Checking…"
              : s.enabled ? `ON · ₹${s.revenue_per_admission || 3000}/admission · ${s.total_sent || 0} sent${s.last_sent_at ? ` · last ${fmtT(s.last_sent_at)}` : ""}`
              : "Send admissions back to Meta so it optimizes for enrolments"}
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {s?.enabled && (
            <button onClick={sendNow} disabled={sending} data-testid="capi-send-now-btn"
              className="inline-flex items-center gap-1.5 rounded-full bg-[#0A0D14] text-white text-[11px] font-bold px-3.5 py-2 hover:bg-black disabled:opacity-50">
              <PaperPlaneTilt size={13} weight="bold" /> {sending ? "Sending…" : "Send now"}
            </button>
          )}
          <button onClick={() => setOpen(true)} title="Configure" data-testid="capi-config-btn"
            className={`${s?.enabled ? "h-8 w-8 rounded-full border border-[#E5E7EB] flex items-center justify-center text-[#525866] hover:bg-[#F3F4F6]" : "inline-flex items-center gap-1.5 rounded-full bg-[#0052FF] text-white text-[11px] font-bold px-3.5 py-2 hover:bg-[#0043d1]"}`}>
            {s?.enabled ? <GearSix size={14} weight="bold" /> : <>Set up</>}
          </button>
        </div>
      </div>
    </div>
  );
};
